export type ReferenceLabelComponents = {
  typeLabel: string;
  number: string;
  /** Optional title suffix from the target block (e.g. "bis"). */
  titleSuffix?: string;
};

export const ReferenceNameTokens = {
  type: '{{type}}',
  number: '{{number}}',
} as const;

const CANONICAL_AUTO_TEMPLATE = `${ReferenceNameTokens.type} ${ReferenceNameTokens.number}`;

/** True when the template is empty or equivalent to pure auto numbering. */
export const isCanonicalAutoTemplate = (
  template: string | null | undefined,
): boolean => {
  if (template == null) return true;
  const trimmed = template.trim();
  if (trimmed.length === 0) return true;
  return trimmed === CANONICAL_AUTO_TEMPLATE;
};

/** Normalize a template for persistence; pure auto becomes null. */
export const normalizeReferenceNameTemplate = (
  template: string | null | undefined,
): string | null => {
  if (template == null) return null;
  const trimmed = template.trim();
  if (trimmed.length === 0 || isCanonicalAutoTemplate(trimmed)) {
    return null;
  }
  return trimmed;
};

/**
 * Substitute `{{type}}` / `{{number}}` in a user template with live components.
 * Literal text without tokens is returned as-is.
 */
export const applyReferenceNameTemplate = (
  template: string,
  components: ReferenceLabelComponents,
  unresolvedNumberFallback = '',
): string => {
  const hasTypeToken = template.includes(ReferenceNameTokens.type);
  const hasNumberToken = template.includes(ReferenceNameTokens.number);

  if (!hasTypeToken && !hasNumberToken) {
    return template.trim();
  }

  const typeValue = components.typeLabel.trim();
  const numberValue =
    components.number.trim().length > 0
      ? components.number.trim()
      : (components.titleSuffix?.trim() ?? '').length > 0
        ? ''
        : unresolvedNumberFallback;

  // Collapse repeated whitespace from empty substitutions.
  let result = template;
  result = result.split(ReferenceNameTokens.type).join(typeValue);
  result = result.split(ReferenceNameTokens.number).join(numberValue);
  result = result.replace(/\s+/g, ' ').trim();

  const titleSuffix = components.titleSuffix?.trim() ?? '';
  if (titleSuffix.length > 0 && !result.includes(titleSuffix)) {
    result = [result, titleSuffix].filter(Boolean).join(' ').trim();
  }

  return result;
};

/**
 * Build the default merged display label from components (no user template).
 */
export const formatReferenceLabelFromComponents = ({
  typeLabel,
  number,
  titleSuffix,
}: ReferenceLabelComponents): string => {
  const title = typeof titleSuffix === 'string' ? titleSuffix.trim() : '';
  return [typeLabel.trim(), number.trim(), title].filter(Boolean).join(' ');
};

export type ReferenceLabelEntry = ReferenceLabelComponents & {
  displayName: string;
};

/** Resolve the final chip label from optional user template + target entry. */
export const resolveReferenceDisplayName = (
  customName: string | null,
  refId: string | null,
  labelEntry: ReferenceLabelEntry | undefined,
): { displayName: string; resolved: boolean } => {
  if (refId === null) {
    return { displayName: '', resolved: false };
  }

  const customNameTrimmed = customName?.trim() ?? '';
  const computedDisplayName = labelEntry?.displayName.trim() ?? '';
  const resolved = computedDisplayName.length > 0;
  const components = labelEntry
    ? {
        typeLabel: labelEntry.typeLabel,
        number: labelEntry.number,
        titleSuffix: labelEntry.titleSuffix,
      }
    : { typeLabel: '', number: '' };

  if (customNameTrimmed.length > 0) {
    const hasTokens =
      customNameTrimmed.includes(ReferenceNameTokens.type) ||
      customNameTrimmed.includes(ReferenceNameTokens.number);
    if (hasTokens) {
      return {
        displayName: applyReferenceNameTemplate(
          customNameTrimmed,
          components,
          refId,
        ),
        resolved,
      };
    }
    return { displayName: customNameTrimmed, resolved: true };
  }

  return {
    displayName: resolved ? computedDisplayName : refId,
    resolved,
  };
};
